import { useState, type Dispatch, type SetStateAction } from "react";
import { useIntl } from "react-intl";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import {
  getRoomDetailsWarnings,
  type RoomWarnings,
} from "src/utils/hooks/useHotelRoomValidation";
import type { RoomDetail } from "../../HotelsContext";
import {
  MAX_ADULTS,
  MAX_CHILDREN_IN_ROOM,
  MAX_GUESTS_IN_ROOM,
} from "../constants";
import { ChildAgeSelect } from "./ChildAgeSelect";
import { CounterAddSubtract } from "./CounterAddSubtract";
import messages from "./RoomDetails.messages";

type RoomDetailsFormProps = {
  roomIndex: number;
  freshFormData: RoomDetail[];
  setFreshFormData: Dispatch<SetStateAction<RoomDetail[]>>;
  maxGuestLimitReached?: boolean;
  maxAdultsLimitReached?: boolean;
  maxChildrenLimitReached?: boolean;
};
export function RoomDetails({
  roomIndex,
  freshFormData,
  setFreshFormData,
  maxGuestLimitReached = false,
  maxAdultsLimitReached = false,
  maxChildrenLimitReached = false,
}: RoomDetailsFormProps) {
  const intl = useIntl();
  const room = freshFormData[roomIndex];
  const roomCounts = {
    adults: room.adults,
    children: room.children.length,
    guests: room.adults + room.children.length,
  };
  // Room warnings don't block submission but provide further detail
  const [roomWarnings, setRoomWarnings] = useState<RoomWarnings>(
    {} as RoomWarnings,
  );

  const handleUpdateAdults = (operator: "add" | "subtract") => {
    operator === "add"
      ? sendAnalyticsInteractionEvent({
          category: "Hotels",
          action: "Click:AddAdult",
        })
      : sendAnalyticsInteractionEvent({
          category: "Hotels",
          action: "Click:SubtractAdult",
        });
    const updatedDetails = [...freshFormData];
    updatedDetails[roomIndex] = {
      ...updatedDetails[roomIndex],
      adults:
        operator === "add"
          ? updatedDetails[roomIndex].adults + 1
          : updatedDetails[roomIndex].adults - 1,
    };
    const warnings = getRoomDetailsWarnings(updatedDetails[roomIndex], intl);
    setRoomWarnings(warnings);
    setFreshFormData(updatedDetails);
  };

  const handleUpdateChildren = (operator: "add" | "subtract") => {
    operator === "add"
      ? sendAnalyticsInteractionEvent({
          category: "Hotels",
          action: "Click:AddChild",
        })
      : sendAnalyticsInteractionEvent({
          category: "Hotels",
          action: "Click:SubtractChild",
        });

    const updatedDetails = [...freshFormData];
    updatedDetails[roomIndex] = {
      ...updatedDetails[roomIndex],
      children:
        operator === "add"
          ? [...updatedDetails[roomIndex].children, { age: undefined }]
          : [...updatedDetails[roomIndex].children].slice(0, -1),
    };
    const warnings = getRoomDetailsWarnings(updatedDetails[roomIndex], intl);
    setRoomWarnings(warnings);
    setFreshFormData(updatedDetails);
  };

  const disableAddAdult =
    maxGuestLimitReached ||
    maxAdultsLimitReached ||
    roomCounts.guests >= MAX_GUESTS_IN_ROOM ||
    roomCounts.adults >= MAX_ADULTS;

  const disableAddChild =
    maxGuestLimitReached ||
    maxChildrenLimitReached ||
    roomCounts.guests >= MAX_GUESTS_IN_ROOM ||
    roomCounts.children >= MAX_CHILDREN_IN_ROOM;

  return (
    <li
      className="flex flex-col pt-3 pb-3 first:pt-0 last:pb-0"
      aria-label={`${intl.formatMessage(messages.room)} ${roomIndex + 1}`}
    >
      <h3 className="text-heading-md select-none">{`${intl.formatMessage(messages.room)} ${
        roomIndex + 1
      }`}</h3>

      <div className="flex items-center justify-between border-b-1 border-b-border-secondary py-2">
        <h4 className="leading-tight">{intl.formatMessage(messages.adults)}</h4>
        <CounterAddSubtract
          count={room.adults}
          handleAdd={() => handleUpdateAdults("add")}
          handleSubtract={() => handleUpdateAdults("subtract")}
          disableMinus={room.adults < 2}
          disablePlus={disableAddAdult}
          passengerType="adult"
        />
      </div>

      <div className="flex items-center justify-between py-2">
        <h4 className="leading-tight">
          {intl.formatMessage(messages.children)}
        </h4>
        <CounterAddSubtract
          count={room.children.length}
          handleAdd={() => handleUpdateChildren("add")}
          handleSubtract={() => handleUpdateChildren("subtract")}
          disableMinus={room.children.length <= 0}
          disablePlus={disableAddChild}
          passengerType="child"
        />
      </div>

      {room.children.length > 0 && (
        <ul className="flex flex-col divide-y-1 divide-neutral-600">
          {room.children.map((child, index) => (
            <ChildAgeSelect
              key={index}
              roomIndex={roomIndex}
              childIndex={index}
              childAge={child.age}
              freshFormData={freshFormData}
              setFreshFormData={setFreshFormData}
            />
          ))}
        </ul>
      )}
      {Object.entries(roomWarnings).map(
        ([key, warning]) =>
          warning.active && (
            <span className="mt-3 rounded-xl bg-surface-caution p-4" key={key}>
              <div>{warning.message}</div>
            </span>
          ),
      )}
    </li>
  );
}
